import {createPersister} from '@github-ui/react-query-persister'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import type {LabelPickerQueryData} from './api-get-search-labels'
import type {Label} from './label'

function isLabel(obj: unknown): obj is Label {
  if (typeof obj !== 'object' || obj === null) return false
  const item = obj as Record<string, unknown>
  return (
    typeof item.id === 'string' &&
    typeof item.color === 'string' &&
    typeof item.name === 'string' &&
    typeof item.nameHTML === 'string' &&
    (typeof item.description === 'string' || item.description === null || item.description === undefined) &&
    typeof item.url === 'string'
  )
}

function isLabelSuggestion(obj: unknown): boolean {
  if (typeof obj !== 'object' || obj === null) return false
  const entry = obj as Record<string, unknown>
  return isLabel(entry.label) && typeof entry.reason === 'string' && typeof entry.rank === 'number'
}

export function isLabelPickerQueryData(obj: unknown): obj is LabelPickerQueryData {
  if (typeof obj !== 'object' || obj === null) return false
  const data = obj as Record<string, unknown>
  return (
    typeof data.repositoryId === 'string' &&
    typeof data.totalCount === 'number' &&
    Array.isArray(data.labels) &&
    data.labels.every(isLabel) &&
    (data.labelsByNames === undefined || (Array.isArray(data.labelsByNames) && data.labelsByNames.every(isLabel))) &&
    (data.suggestedLabels === undefined ||
      (Array.isArray(data.suggestedLabels) && data.suggestedLabels.every(isLabelSuggestion)))
  )
}

const LabelPickerQueryDataValidator = {
  Check: (value: unknown): value is LabelPickerQueryData => isLabelPickerQueryData(value),
  Code: () => 'manual-validator',
  *Errors() {
    /* no-op */
  },
}

/**
 * Persister for the stable, repo-wide label list only.
 *
 * Suggestions are deliberately NOT persisted: they encode how recently and how
 * often *this viewer* used a label, so replaying a day-old snapshot from
 * IndexedDB would mask exactly the signal the group exists to surface. The
 * suggestions query runs without a persister and revalidates from the network.
 */
export function createLabelPersister(sendAnalyticsEvent: SendAnalyticsEventFunction) {
  return createPersister({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validator: LabelPickerQueryDataValidator as any,
    sendAnalyticsEvent,
    enabled: true,
  })
}
